/**
 * Dosyalar — a screen, not a drawer.
 *
 * The first version of this was a panel docked under the terminal wall in Canlı
 * Operasyon. That was wrong twice over: that screen is already the densest in
 * the console, and a strip under a wall of terminals cannot be the thing an
 * operator moves a four-gigabyte file with. It is a separate destination now,
 * beside `/terminals`, for the reason that screen gives about itself — it
 * answers a separate question.
 *
 * # The shape is FileZilla's, and where it cannot be, it says so
 *
 * Two panes, local and remote, each with its own path, its own sortable columns
 * and its own selection; arrows between them; a queue underneath with the
 * transfers in it. That layout is thirty years old because it is right: the
 * operator is comparing two places and moving things between them, and every
 * other arrangement hides one half of that.
 *
 * The one place a browser cannot follow is the local pane, and the honest answer
 * is two answers (see `local.ts`). Where the File System Access API exists, the
 * left pane is a real directory: it browses, and a download lands *in it*. Where
 * it does not, the left pane is a tray of picked files and downloads go wherever
 * the browser puts them. The header says which, because a list the page cannot
 * browse labelled "local files" would be the lie this rebuild exists to end.
 *
 * # What is deliberately not here
 *
 * No editor, no preview, no chmod. Everyone who reaches this screen also has
 * `/terminals`, where `vim` and `chmod` are already open and better.
 *
 * Rename, delete and new-folder *are* here, on the remote pane only, and only
 * since ADR-0027 §1 — which withdrew §6's refusal of the first two rather than
 * quietly contradicting it. This paragraph said they were absent for a while
 * after they arrived, which is the exact failure CLAUDE.md warns about: a
 * document asserting a subsystem that is no longer true. The local pane is
 * still deliberately excluded (ADR-0027 §1), and a recursive delete is a client
 * loop over one confined act per node, never `rm -r` (§2).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  CornerLeftUp,
  File as FileIcon,
  Folder,
  FolderOpen,
  FolderPlus,
  HardDrive,
  Link2,
  Lock,
  PenLine,
  Plus,
  RefreshCw,
  Server,
  Settings2,
  Trash2,
  X,
} from 'lucide-react'

import { Icon } from '@/components/icon'
import { Badge, Button, EmptyState, Page, PageHeader, StatusDot } from '@/components/primitives'
import {
  CheckboxField,
  Dialog,
  Notice,
  QueryState,
  SelectField,
  TextField,
  useApiErrorMessage,
} from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { useLocale, useT } from '@/i18n/context'
import { formatBytes, formatDuration, formatRelative } from '@/i18n'
import { useEditable } from '@/hooks/useEditable'
import { useNow } from '@/hooks/useElapsed'
import { useMediaQuery } from '@/hooks/usePointerKind'
import { useSplitter } from '@/hooks/useSplitter'
import type { ID } from '@/types/domain'
import {
  abortUpload,
  appendChunk,
  createDir,
  downloadUrl,
  finishUpload,
  listFiles,
  listRoots,
  makeDir,
  moveEntry,
  probeUpload,
  reasonOf,
  removeEntry,
  saveMany,
  walkTree,
  type HostFileEntry,
  type HostRoot,
} from './api'
import { HostRootsDialog } from './HostRootsDialog'
import {
  ensureLocalPath,
  ensureWritable,
  localMode,
  pickLocalDirectory,
  readLocalDir,
  saveIntoLocalDir,
  walkLocalDir,
  type LocalDirHandle,
  type LocalEntry,
} from './local'
import {
  DEFAULT_PARALLEL,
  MAX_PARALLEL,
  PARALLEL_CHOICES,
  batchEtaOf,
  batchRateOf,
  batchTotals,
  etaOf,
  fileDigest,
  isActive,
  rateOf,
  reduceTransfers,
  relPath,
  remainingChunks,
  type Transfer,
  type TimedEvent,
} from './transfers'
import { useTransferPool } from './pool'
import { planRemoval, type RemovalRefusal } from './remove'
import './Files.css'

type SortKey = 'name' | 'size' | 'modified'
type SortDir = 'asc' | 'desc'

interface Sort {
  key: SortKey
  dir: SortDir
}

/** A row either pane can render, so the two lists are one component and cannot
 *  drift apart in how they sort, select or read. */
interface Row {
  name: string
  dir: boolean
  size: number
  /** Epoch milliseconds; 0 means nothing measured one. */
  modified: number
  mode?: string
  /** A symlink leading out of the root, or a credential-shaped name. Both are
   *  shown and neither is transferable — the server refuses them and the row
   *  says why rather than disappearing. */
  blocked?: 'outside' | 'denied'
  symlink?: boolean
}

function sortRows(rows: Row[], sort: Sort): Row[] {
  const out = [...rows]
  const sign = sort.dir === 'asc' ? 1 : -1
  out.sort((a, b) => {
    // Directories first in every order. "Up one level and into another" is
    // navigation, and navigation must not move under somebody who changed what
    // they are sorting by.
    if (a.dir !== b.dir) return a.dir ? -1 : 1
    switch (sort.key) {
      case 'size':
        return sign * (a.size - b.size)
      case 'modified':
        return sign * (a.modified - b.modified)
      default:
        return sign * a.name.localeCompare(b.name, 'tr')
    }
  })
  return out
}

/**
 * A transfer's state in words.
 *
 * Switched rather than built as `hostfiles.state.${state}`. An interpolated key
 * is invisible both to the guard that counts unread messages and to the type
 * that makes a missing translation a compile error, so it turns the console's
 * whole i18n guarantee off for exactly the strings it touches.
 */
function stateLabel(state: Transfer['state'], t: ReturnType<typeof useT>): string {
  switch (state) {
    case 'queued':
      return t('hostfiles.state.queued')
    case 'hashing':
      return t('hostfiles.state.hashing')
    case 'sending':
      return t('hostfiles.state.sending')
    case 'finishing':
      return t('hostfiles.state.finishing')
    case 'done':
      return t('hostfiles.state.done')
    case 'failed':
      return t('hostfiles.state.failed')
    case 'cancelled':
      return t('hostfiles.state.cancelled')
    case 'skipped':
      return t('hostfiles.state.skipped')
  }
}

function parentOf(path: string): string {
  const trimmed = path.replace(/\/+$/, '')
  const i = trimmed.lastIndexOf('/')
  return i <= 0 ? '' : trimmed.slice(0, i)
}

/* ------------------------------------------------------------------ A pane */

interface PaneProps {
  title: string
  icon: typeof Server
  /** What is above the list: a path, a picker, a badge. */
  head: React.ReactNode
  rows: Row[]
  sort: Sort
  onSort: (key: SortKey) => void
  selected: Set<string>
  onSelect: (name: string, additive: boolean) => void
  onOpen: (row: Row) => void
  onTransfer: (row: Row) => void
  pending?: boolean
  error?: unknown
  onRetry?: () => void
  empty: string
  emptyHint?: string
  /** Drop target, when this pane can receive. */
  onDropFiles?: (files: FileList) => void
}

function Pane({
  title,
  icon,
  head,
  rows,
  sort,
  onSort,
  selected,
  onSelect,
  onOpen,
  onTransfer,
  pending,
  error,
  onRetry,
  empty,
  emptyHint,
  onDropFiles,
}: PaneProps) {
  const t = useT()
  const { locale } = useLocale()
  const [over, setOver] = useState(false)

  const column = (key: SortKey, label: string) => (
    <button
      type="button"
      className={clsx('k-fm__col', sort.key === key && 'k-fm__col--on')}
      onClick={() => onSort(key)}
      aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sort.key === key && <span aria-hidden>{sort.dir === 'asc' ? ' ▲' : ' ▼'}</span>}
    </button>
  )

  return (
    <section className="k-fm__pane" aria-label={title}>
      <header className="k-fm__panehead">
        <Icon icon={icon} size={14} />
        <span className="k-fm__panetitle">{title}</span>
        {head}
      </header>

      <div className="k-fm__cols">
        {column('name', t('hostfiles.colName'))}
        {column('size', t('hostfiles.colSize'))}
        {column('modified', t('hostfiles.colModified'))}
      </div>

      <div
        className={clsx('k-fm__list', over && onDropFiles && 'k-fm__list--over')}
        onDragOver={(e) => {
          if (!onDropFiles) return
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          if (!onDropFiles) return
          e.preventDefault()
          setOver(false)
          onDropFiles(e.dataTransfer.files)
        }}
      >
        <QueryState pending={Boolean(pending)} error={error} onRetry={onRetry}>
          {rows.length === 0 ? (
            <EmptyState title={empty} hint={emptyHint} />
          ) : (
            <ul className="k-fm__rows">
              {rows.map((r) => (
                <li
                  key={r.name}
                  className={clsx(
                    'k-fm__row',
                    selected.has(r.name) && 'k-fm__row--on',
                    r.blocked && 'k-fm__row--blocked',
                  )}
                  onClick={(e) => onSelect(r.name, e.ctrlKey || e.metaKey)}
                  onDoubleClick={() => (r.dir ? onOpen(r) : onTransfer(r))}
                  title={
                    r.blocked === 'outside'
                      ? t('hostfiles.outside')
                      : r.blocked === 'denied'
                        ? t('hostfiles.deniedHint')
                        : undefined
                  }
                >
                  <span className="k-fm__name">
                    <Icon
                      icon={r.dir ? Folder : r.symlink ? Link2 : FileIcon}
                      size={14}
                    />
                    <span className="k-fm__nametext">{r.name}</span>
                    {r.blocked === 'denied' && (
                      <Badge tone="neutral" subtle>
                        <Icon icon={Lock} size={11} /> {t('hostfiles.denied')}
                      </Badge>
                    )}
                    {r.blocked === 'outside' && (
                      <Badge tone="warn" subtle>
                        {t('hostfiles.outside')}
                      </Badge>
                    )}
                  </span>
                  <span className="k-fm__size k-mono">{r.dir ? '' : formatBytes(r.size)}</span>
                  <span className="k-fm__when k-mono">
                    {r.modified > 0 ? formatRelative(new Date(r.modified).toISOString(), locale) : '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </QueryState>
      </div>
    </section>
  )
}

/* ---------------------------------------------------------------- The page */

/**
 * The question an upload has to ask before it replaces something.
 *
 * Its own component rather than `useConfirm`, because the answer has three
 * shapes and not two: replace, skip, and *apply that to the rest*. The third is
 * not a convenience — sending the same folder a second time would otherwise put
 * fifty-eight questions in front of somebody, and a person answering
 * fifty-eight dialogs stops reading them by the fourth, which is worse than not
 * asking at all.
 */
function ReplaceDialog({
  name,
  answer,
}: {
  name: string
  answer: (choice: 'replace' | 'skip', all: boolean) => void
}) {
  const t = useT()
  const [all, setAll] = useState(false)
  return (
    <Dialog
      title={t('hostfiles.replaceTitle')}
      // Closing it is a skip, not a silent overwrite. Where a dismissal has to
      // mean something, it means the option that changes nothing.
      onClose={() => answer('skip', all)}
      footer={
        <>
          <Button onClick={() => answer('skip', all)}>{t('hostfiles.replaceSkip')}</Button>
          <Button variant="danger" onClick={() => answer('replace', all)}>
            {t('hostfiles.replaceYes')}
          </Button>
        </>
      }
    >
      <p>{t('hostfiles.replaceAsk', { name })}</p>
      <CheckboxField label={t('hostfiles.replaceAll')} checked={all} onChange={setAll} />
    </Dialog>
  )
}

/**
 * Why a download was refused, in the reader's language.
 *
 * The download is a plain `fetch` rather than a call through the JSON client —
 * deliberately, so a two-gigabyte core dump never exists in a tab's memory — and
 * that is also why it is the one path on this screen with no `ApiError` to read.
 * The refusal still carries the same `reason` word every other endpoint sends,
 * so it is read off the body here rather than left as a status code.
 */
async function downloadRefusal(res: Response, t: ReturnType<typeof useT>): Promise<string> {
  let reason: string | undefined
  try {
    const body = (await res.json()) as { fields?: { reason?: string } }
    reason = body.fields?.reason
  } catch {
    // Not JSON: a proxy, or the server gone. The status is then genuinely all
    // there is to say, and inventing a cause would be worse than saying so.
  }
  switch (reason) {
    case 'deniedName':
      return t('hostfiles.deniedHint')
    case 'outsideRoot':
      return t('hostfiles.outsideHint')
    case 'noRoot':
      return t('hostfiles.opNoRoot')
    case 'notAFile':
      return t('hostfiles.opNotAnEntry')
    default:
      return t('hostfiles.downloadFailed', { status: String(res.status) })
  }
}

/**
 * Why a delete will not be attempted, in the reader's language.
 *
 * A `switch` over the discriminated union with an exhaustiveness check, not an
 * `if` chain and never an interpolated key: a missing branch here would be a
 * destructive gesture that refused with no sentence, and an interpolated key is
 * invisible both to the type that makes a missing translation a compile error
 * and to the guard that finds strings nothing reads.
 */
function removalRefusal(r: RemovalRefusal, name: string, t: ReturnType<typeof useT>): string {
  switch (r.refusal) {
    case 'truncated':
      return t('hostfiles.removeTruncated', { name })
    case 'too-many':
      return t('hostfiles.removeTooMany', { name, count: String(r.count) })
    case 'denied':
      return t('hostfiles.removeDenied', { item: r.item })
    case 'root':
      return t('hostfiles.removeRoot')
    default: {
      const never: never = r
      return String(never)
    }
  }
}

export function FilesPage() {
  const t = useT()
  const { locale } = useLocale()
  const describe = useApiErrorMessage()
  const confirm = useConfirm()
  const queryClient = useQueryClient()
  const now = useNow(1000)

  const mode = useMemo(() => localMode(), [])

  /* Two panes an operator can size, and a queue band they can grow.
   *
   * The stored value for the panes is a *ratio*, not a left width: two peers
   * being compared must both give ground when the window changes, which a fixed
   * left width does not do. Files.css carries the rest of that argument, and the
   * comment there that used to say the split was deliberately fixed.
   *
   * Below 900px the panes stack, and a ratio between two columns means nothing
   * when there is one column — so the separators' role, tab stop and handlers
   * are dropped here at the same width the stylesheet stacks at. Read as state
   * rather than left to CSS because a `display: none` on the vertical gutter
   * would take the transfer arrows with it: they are its children. */
  const narrow = useMediaQuery('(max-width: 900px)')

  const paneSplit = useSplitter({
    storageKey: 'kimene.fm.split',
    axis: 'x',
    initial: 0.5,
    min: 0.2,
    max: 0.8,
    step: 0.02,
    label: t('hostfiles.resizePanes'),
    valueText: (v) => t('hostfiles.resizePanesValue', { pct: String(Math.round(v * 100)) }),
    // Measured off the two panes at pointerdown rather than derived from the
    // template: the free space excludes the arrows column and both gaps, and a
    // number computed from the template is one that quietly stops matching it.
    origin: (g) => {
      const a = (g.previousElementSibling as HTMLElement | null)?.getBoundingClientRect().width ?? 0
      const b = (g.nextElementSibling as HTMLElement | null)?.getBoundingClientRect().width ?? 0
      return { a, free: a + b }
    },
    toValue: (o, dx) => (o.free > 0 ? (o.a + dx) / o.free : 0.5),
  })

  const queueSplit = useSplitter({
    storageKey: 'kimene.fm.queue',
    axis: 'y',
    initial: 192,
    min: 72,
    max: 720,
    step: 16,
    label: t('hostfiles.resizeQueue'),
    valueText: (v) => t('hostfiles.resizeQueueValue', { px: String(Math.round(v)) }),
    // Two clamps on purpose. min/max above are constants, so the announced range
    // is honest and a size stored on a 4K display is not absurd on a laptop.
    // This one is live — but only for a *drag*: `useSplitter.set` never routes
    // through `toValue`, so `End` writes 720 whatever is on the screen, and the
    // value read back from storage on the next load has never met this ceiling
    // at all. It used to say the queue could never eat the panes; measured at
    // 1440x900 a stored 720 left both panes 76px tall with a 14px list. The
    // bound that actually holds is the `min(…, calc(100vh - 320px))` on
    // `.k-fm__queue`, which is where a stale preference cannot get past it.
    origin: (g, current) => ({
      v: current,
      ceiling: Math.max(
        72,
        ((g.closest('.k-fm') as HTMLElement | null)?.getBoundingClientRect().height ?? 0) - 220,
      ),
    }),
    // Dragging *up* makes the queue taller, so the delta is subtracted.
    toValue: (o, _dx, dy) => Math.min(o.v - dy, o.ceiling),
  })

  const roots = useQuery({ queryKey: ['host-roots'], queryFn: listRoots })
  const rootItems = useMemo(() => roots.data?.items ?? [], [roots.data])

  const [rootId, setRootId] = useState<ID>('')
  const [remotePath, setRemotePath] = useState('')
  const [remoteSort, setRemoteSort] = useState<Sort>({ key: 'name', dir: 'asc' })
  const [remotePick, setRemotePick] = useState<Set<string>>(new Set())
  const [managing, setManaging] = useState(false)
  const [pathError, setPathError] = useState('')

  // Answered once when the list first arrives. A refetch that re-selected the
  // first root would move an operator out of the directory they were reading.
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current || rootItems.length === 0) return
    seeded.current = true
    setRootId(rootItems[0]?.id ?? '')
  }, [rootItems])

  const root: HostRoot | undefined = rootItems.find((r) => r.id === rootId)

  /* Seeded from where the pane currently is, and it stops following the moment
     somebody types — `useEditable`, not an effect that re-seeds (CLAUDE.md). */
  const typed = useEditable(root ? `${root.path}${remotePath ? '/' + remotePath : ''}` : '')

  const jump = () => {
    const target = typed.value.trim().replace(/\/+$/, '')
    if (target === '') return
    // The longest matching root wins: /ssd/data/project/kimene is a better
    // answer than /ssd/data/project for a path inside the former.
    const match = [...rootItems]
      .filter((r) => target === r.path || target.startsWith(r.path.replace(/\/+$/, '') + '/'))
      .sort((a, b) => b.path.length - a.path.length)[0]
    if (!match) {
      setPathError(t('hostfiles.pathNotInAnyRoot'))
      return
    }
    setPathError('')
    setRootId(match.id)
    setRemotePath(target.slice(match.path.replace(/\/+$/, '').length).replace(/^\/+/, ''))
    setRemotePick(new Set())
    typed.reset()
  }

  const listing = useQuery({
    queryKey: ['host-files', rootId, remotePath],
    queryFn: ({ signal }) => listFiles(rootId, remotePath, signal),
    enabled: rootId !== '',
  })

  const remoteRows = useMemo<Row[]>(
    () =>
      sortRows(
        (listing.data?.entries ?? []).map((e: HostFileEntry) => ({
          name: e.name,
          dir: e.dir,
          size: e.size,
          modified: Date.parse(e.modTime) || 0,
          mode: e.mode,
          symlink: e.symlink,
          blocked: e.outside ? ('outside' as const) : e.denied ? ('denied' as const) : undefined,
        })),
        remoteSort,
      ),
    [listing.data, remoteSort],
  )
  const partials = listing.data?.partials ?? []
  const writable = listing.data?.writable ?? root?.writable ?? false

  /* ---- local side ---- */
  const [localDir, setLocalDir] = useState<LocalDirHandle | null>(null)
  const [localTrail, setLocalTrail] = useState<LocalDirHandle[]>([])
  const [localRows, setLocalRows] = useState<LocalEntry[]>([])
  const [localSort, setLocalSort] = useState<Sort>({ key: 'name', dir: 'asc' })
  const [localPick, setLocalPick] = useState<Set<string>>(new Set())
  const [tray, setTray] = useState<File[]>([])
  const [localBusy, setLocalBusy] = useState(false)
  const [localError, setLocalError] = useState('')

  const here = localTrail[localTrail.length - 1] ?? localDir

  const readHere = useCallback(async (dir: LocalDirHandle | null) => {
    if (!dir) {
      setLocalRows([])
      return
    }
    setLocalBusy(true)
    try {
      setLocalRows(await readLocalDir(dir))
      setLocalError('')
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : String(err))
    } finally {
      setLocalBusy(false)
    }
  }, [])

  useEffect(() => {
    void readHere(here)
  }, [here, readHere])

  const localList = useMemo<Row[]>(() => {
    if (mode === 'tray') {
      return sortRows(
        tray.map((f) => ({ name: f.name, dir: false, size: f.size, modified: f.lastModified })),
        localSort,
      )
    }
    return sortRows(
      localRows.map((e) => ({ name: e.name, dir: e.dir, size: e.size, modified: e.modified })),
      localSort,
    )
  }, [mode, tray, localRows, localSort, localSort.dir])

  /* ---- transfers ---- */
  const [transfers, setTransfers] = useState<Transfer[]>([])
  const dispatch = useCallback(
    (e: TimedEvent) => setTransfers((list) => reduceTransfers(list, e)),
    [],
  )
  const [tab, setTab] = useState<'active' | 'failed' | 'done'>('active')

  /* Memoised on the root it names, and never captured behind an empty dependency
     array. The pool's drain used to be handed `useCallback(() => refresh(), [])`,
     which froze the `refresh` of the first render — where `rootId` is still ''
     because the list has not arrived yet. `['host-files', '']` is a prefix of
     nothing, so the one refetch ADR-0027 §5 promises when the queue empties
     invalidated no query at all and the pane never showed what had just landed
     on it. The pool keeps this in a ref it reassigns every render, so passing it
     directly is what makes the drain refresh the root the operator is in. */
  const refresh = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: ['host-files', rootId] }),
    [queryClient, rootId],
  )

  const toggleSort = (set: (s: Sort) => void, cur: Sort) => (key: SortKey) =>
    set(cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })

  const pickIn = (setter: (fn: (p: Set<string>) => Set<string>) => void) =>
    (name: string, additive: boolean) =>
      setter((prev) => {
        const next = additive ? new Set(prev) : new Set<string>()
        if (additive && prev.has(name)) next.delete(name)
        else next.add(name)
        return next
      })

  /* ---- the pool, the setting, and the question an upload has to ask ---- */

  const [limit, setLimit] = useState<number>(() => {
    const stored = Number(localStorage.getItem('kimene.fm.parallel'))
    return Number.isFinite(stored) && stored > 0 ? Math.min(stored, MAX_PARALLEL) : DEFAULT_PARALLEL
  })

  /**
   * Replace what is there, or leave it?
   *
   * Until ADR-0027 §6 an upload replaced an existing file without a word — the
   * act `hostfs.Move` has always refused on a rename. The host now says no, and
   * this asks.
   *
   * "Apply to the rest" is not a convenience: sending the same folder a second
   * time would otherwise put fifty-eight questions in front of somebody. The
   * answer lives in a ref rather than in state because the pool's workers read
   * it between awaits, and a re-render is not something a worker waits for.
   *
   * # Why the questions queue, and why the answer is stamped with a batch
   *
   * There are three workers and one dialog. Keeping the resolver *in* the dialog
   * state meant the second worker to reach a 409 overwrote the first one's
   * question, and the first one's promise was then never settled by anything:
   * its job never returned, so the pool never released the id, and a queue of
   * two hundred files stopped dead with no message. So the questions wait in a
   * ref, one dialog at a time, and every one of them is eventually answered.
   *
   * And "the rest" means the rest of *this gesture*. A bare ref outlived the
   * batch it was given in, so a `replace` answered for a folder was still in
   * force when somebody dropped an unrelated file on the pane an hour later —
   * silently overwriting a file nobody had been asked about, which is the act
   * ADR-0027 §6 exists to stop.
   */
  const overwriteAll = useRef<{ batch: number; choice: 'replace' | 'skip' } | null>(null)
  const batchSeq = useRef(0)
  const asks = useRef<Array<{ name: string; batch: number; resolve: (c: 'replace' | 'skip') => void }>>([])
  const [replaceAsk, setReplaceAsk] = useState<{ name: string; batch: number } | null>(null)

  /** One gesture — an arrow, a double-click, a drop. What "apply to the rest"
   *  applies to, and nothing wider. */
  const newBatch = useCallback(() => {
    batchSeq.current += 1
    return batchSeq.current
  }, [])

  /**
   * Why a refusal is composed here rather than rendered from the server.
   *
   * The endpoint sends a *word* and an English sentence; the word travels and
   * the sentence is a fallback for anything reading this API without a
   * translation table. This is the rule ADR-0026 §10 set for this module after
   * an operator met English prose in a Turkish console, applied to the refusals
   * ADR-0027 adds — four of which share one status and differ only in that
   * English.
   *
   * It sits above the transfer pool rather than beside the three buttons it was
   * written for, and the placement is the point. The pool and the two mirror
   * arrows meet exactly the same refusals, and all three read the wire's English
   * straight onto the screen while this function sat further down the file
   * translating those same words for the buttons: a folder fetched from a root
   * whose disk had gone put "That root does not exist on its host." in the
   * notice strip, measured. A rule that holds on three call sites out of six is
   * not a rule.
   */
  const refusalSentence = useCallback(
    (err: unknown): string => {
      switch (reasonOf(err)) {
        case 'notEmpty':
          return t('hostfiles.opNotEmpty')
        case 'exists':
          return t('hostfiles.opNameTaken')
        case 'readOnly':
          // Not `readOnlyRoot`: that key is the pane's two-word badge, and as a
          // refusal it left the notice strip reading “salt okunur” — a property
          // of the root, never a statement of what the click did. Reachable
          // exactly the way api.ts's makeDir comment describes: the listing says
          // writable, somebody flips it, and every control on the pane is still
          // live against a root that will now refuse them.
          return t('hostfiles.opReadOnly')
        case 'deniedName':
          return t('hostfiles.deniedHint')
        case 'outsideRoot':
          return t('hostfiles.pathNotInAnyRoot')
        // The fourth 409 on this surface, and the one that was missing. `mkdir`,
        // `mv` and `rmdir` all begin with ScriptRoot's `[ -d "$root" ] || exit 5`,
        // so an unmounted disk or a moved directory refuses every one of these —
        // and it reached a Turkish console as the wire's own English sentence,
        // which is precisely what this function exists to stop.
        case 'noRoot':
          return t('hostfiles.rootGone')
        case 'notAFile':
          return t('hostfiles.opNotAnEntry')
        // Reachable from the transfer path and from nowhere else, which is the
        // second reason the pool routes through here: finishScript refuses a
        // directory at the destination with exit 6, so an upload whose name is
        // already a folder on the host lands on this branch. From the three
        // buttons alone it could never be taken — ScriptResolveParent exits 6
        // only on `.` and `..`, and CleanRelative refuses the second while
        // Mkdir/Move/Remove refuse the first before the script runs.
        // Reachable here and nowhere else, for the same reason: finishUpload
        // rethrows everything that is not 'exists', so a 502 from a digest that
        // did not match lands in the pool's catch and in no button's.
        case 'checksum':
          return t('hostfiles.opChecksum')
        // 'offsetMismatch' is the one reason deliberately left out, and it is
        // genuinely unreachable rather than merely unhandled: appendChunk reads
        // the host's own offset off that 409 and resumes from it, so it is
        // answered by asking where the host is, never by a sentence.
        default:
          return describe(err).title
      }
    },
    [t, describe],
  )

  const askReplace = useCallback(
    (name: string, batch: number) =>
      new Promise<'replace' | 'skip'>((resolve) => {
        const already = overwriteAll.current
        if (already && already.batch === batch) {
          resolve(already.choice)
          return
        }
        asks.current.push({ name, batch, resolve })
        if (asks.current.length === 1) setReplaceAsk({ name, batch })
      }),
    [],
  )

  const answerReplace = useCallback((choice: 'replace' | 'skip', all: boolean) => {
    const head = asks.current.shift()
    if (head) {
      head.resolve(choice)
      if (all) {
        overwriteAll.current = { batch: head.batch, choice }
        // What is already waiting from the same gesture is answered with it. A
        // question from another gesture is somebody else's and keeps its turn.
        asks.current = asks.current.filter((a) => {
          if (a.batch !== head.batch) return true
          a.resolve(choice)
          return false
        })
      }
    }
    const next = asks.current[0]
    setReplaceAsk(next ? { name: next.name, batch: next.batch } : null)
  }, [])

  const pool = useTransferPool(
    transfers,
    limit,
    useCallback((id: string) => dispatch({ kind: 'cancelled', id }), [dispatch]),
    // One refetch when the queue empties, on both sides. Per-file it was one
    // invalidation per transfer, three landing at once, each refetching a
    // listing over a fresh connection to the host the transfers had just
    // finished loading — and on the local pane a full re-read of the chosen
    // directory after every downloaded file, which is one getFile() per entry
    // each time and so quadratic in the size of a mirrored folder.
    useCallback(() => {
      refresh()
      void readHere(here)
    }, [refresh, readHere, here]),
  )
  const { enqueue, cancel, cancelAll, gate } = pool

  /** One file up, in parts the host agreed to.
   *
   *  `rel` is the whole path under the root rather than a name joined to the
   *  current directory, because a folder transfer writes into subdirectories the
   *  operator is not standing in. `name` stays separate: it is what the queue
   *  row says, and for a nested file that is the path, not the basename. */
  const upload = useCallback(
    async (file: File, rel: string, name: string, batch: number, signal: AbortSignal) => {
      const id = `up:${rootId}:${rel}`
      try {
        dispatch({ kind: 'hashing', id })
        const sha256 = await fileDigest(file)
        let have = (await probeUpload(rootId, rel, signal)).offset
        dispatch({ kind: 'resumed', id, have })
        for (;;) {
          const rest = remainingChunks(file.size, have)
          const chunk = rest[0]
          if (!chunk) break
          const res = await appendChunk(
            rootId, rel, chunk.start, file.slice(chunk.start, chunk.end), signal,
          )
          if (!res.ok) {
            have = res.have
            dispatch({ kind: 'resumed', id, have })
            continue
          }
          have = chunk.end
          dispatch({ kind: 'progress', id, sent: have })
          if (file.size === 0) break
        }
        dispatch({ kind: 'finishing', id })
        // Serialised against every other finish. A finish is a whole-file
        // sha256sum on the host under a 60-second command timeout; three of
        // those contending for one disk is how a row fails *after* every byte
        // arrived, which is the worst failure a transfer queue can produce.
        try {
          await gate(() => finishUpload(rootId, rel, sha256, { signal }))
        } catch (err) {
          // Only 'exists'. Three refusals share this status here, and reading
          // the status rather than the reason is how "that root is read-only"
          // would become "replace it?".
          if (reasonOf(err) !== 'exists') throw err
          const choice = await askReplace(name, batch)
          // Nothing here watches the signal, so an abort that arrives while the
          // question is open is only seen at this line. It cannot arrive from a
          // click today — the question is modal, so every stop button on the
          // page is behind `inert` until it is answered — and that is a property
          // of a dialog component in another module rather than of this rule.
          // Kept because of what it guards: `skip` calls `abortUpload`, and
          // ADR-0027 §5 is explicit that a cancel must *never* do that, since
          // the partial on the host is what a resume is built on. The day the
          // question stops being modal, the difference between "I withdrew this"
          // and "these bytes do not belong there" would otherwise be decided by
          // whichever the operator clicked last.
          if (signal.aborted) throw new DOMException('cancelled', 'AbortError')
          if (choice === 'skip') {
            // Discarded, unlike a cancel. A cancel may be resumed and the
            // partial is deliberately left for that; a skip is the operator
            // saying these bytes do not belong there, and leaving a
            // .kimene-part file behind for a month is how that becomes
            // somebody else's puzzle.
            await abortUpload(rootId, rel)
            dispatch({ kind: 'skipped', id })
            return 'skipped'
          }
          await gate(() => finishUpload(rootId, rel, sha256, { overwrite: true, signal }))
        }
        dispatch({ kind: 'done', id })
        return 'done'
      } catch (err) {
        // Checked before describe(), because an abort is not a failure and
        // rendering it as one is the transient-versus-persistent conflation
        // this codebase has an invariant about. It is also what finally makes
        // 'cancelled' reachable — until now it was a state nothing produced.
        if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
          dispatch({ kind: 'cancelled', id })
          return 'cancelled'
        }
        dispatch({ kind: 'failed', id, error: refusalSentence(err) })
        return 'failed'
      }
    },
    [rootId, dispatch, refusalSentence, gate, askReplace],
  )

  /** One file down. Into a chosen folder where the browser allows it, so the
   *  bytes land where the operator is looking; otherwise the browser's own
   *  download, which is the most any of the others offer.
   *
   *  `into` is passed rather than read from `here` so that a folder transfer can
   *  aim each file at the subdirectory it belongs in. */
  const download = useCallback(
    async (rel: string, name: string, into: LocalDirHandle, signal: AbortSignal) => {
      const id = `down:${rootId}:${rel}`
      try {
        if (!(await ensureWritable(into))) throw new Error(t('hostfiles.localDenied'))
        dispatch({ kind: 'resumed', id, have: 0 })
        const res = await fetch(downloadUrl(rootId, rel), { credentials: 'same-origin', signal })
        // A raw `HTTP 403` in the queue row is the same defect ADR-0026 §10
        // records for the registration check: the console's own sentence, from
        // the word the server sends, rather than a status an operator has to
        // look up. The body is JSON for every refusal this API makes; a
        // response that is not is a proxy or a crash, and the status is then
        // genuinely all there is to say.
        if (!res.ok || !res.body) throw new Error(await downloadRefusal(res, t))
        await saveIntoLocalDir(
          into, name, res.body, (bytes) => dispatch({ kind: 'progress', id, sent: bytes }), signal,
        )
        dispatch({ kind: 'done', id })
        return 'done'
      } catch (err) {
        if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
          dispatch({ kind: 'cancelled', id })
          return 'cancelled'
        }
        dispatch({ kind: 'failed', id, error: err instanceof Error ? err.message : String(err) })
        return 'failed'
      }
    },
    [rootId, dispatch, t],
  )

  /**
   * What a folder costs, and why these two run one file at a time.
   *
   * Every transfer here is an SSH exec on the far host. The per-file selection
   * used to fire them all at once, which is survivable for the three or four
   * files somebody ctrl-clicks and is not survivable for a folder: two hundred
   * concurrent execs is a fork bomb aimed at a machine the fleet is working on.
   * So both directions walk their queue sequentially — slower than it could be,
   * and the alternative is worse than slow.
   *
   * The other half is that a *folder* transfer must not start at all unless the
   * whole shape is known. A truncated walk mirrored file by file produces a
   * directory that looks complete and is not, which is the failure that gets
   * discovered a week later by whatever read the missing file.
   */
  const [folderBusy, setFolderBusy] = useState(false)
  /* Separate from localError, which belongs to *reading* the chosen folder and
     is cleared every time that succeeds. Putting a transfer's outcome there
     meant the refresh at the end of the transfer erased the sentence explaining
     it, about a hundred milliseconds after it appeared. */
  const [transferNote, setTransferNote] = useState('')

  /**
   * A queued upload, registered with the pool.
   *
   * The row is dispatched here rather than inside `upload` so it exists as
   * `queued` before anything can start it — the pool admits from the reduced
   * list, and a job whose row has not landed yet is a job it cannot see.
   */
  const queueUpload = (file: File, rel: string, name: string, batch: number) => {
    const id = `up:${rootId}:${rel}`
    dispatch({
      kind: 'queued',
      transfer: {
        id, direction: 'up', rootId, path: rel, name,
        size: file.size, sent: 0, state: 'queued',
      },
    })
    enqueue(id, (signal) => upload(file, rel, name, batch, signal))
  }

  const queueDownload = (rel: string, name: string, size: number, into: LocalDirHandle) => {
    const id = `down:${rootId}:${rel}`
    dispatch({
      kind: 'queued',
      transfer: { id, direction: 'down', rootId, path: rel, name, size, sent: 0, state: 'queued' },
    })
    // No per-file re-read of the local pane: the drain does it once, for the
    // same reason the remote listing is refetched once.
    enqueue(id, (signal) => download(rel, name, into, signal))
  }

  const sendRows = async (picked: LocalEntry[]) => {
    if (!writable || folderBusy) return
    setFolderBusy(true)
    setTransferNote('')
    const batch = newBatch()
    // Collected, not written as they happen. `transferNote` is one slot: a
    // second refused folder used to overwrite the first one's sentence, and the
    // operator was left believing one folder had been refused when two were.
    const notes: string[] = []
    try {
      for (const e of picked) {
        if (!e.dir) {
          const f = await e.file?.getFile()
          if (f) queueUpload(f, relPath(remotePath, e.name), e.name, batch)
          continue
        }
        if (!e.directory) continue
        const { nodes, truncated } = await walkLocalDir(e.directory)
        if (truncated) {
          notes.push(t('hostfiles.treeTooBig', { name: e.name }))
          continue
        }
        const base = relPath(remotePath, e.name)
        await makeDir(rootId, base)
        // Directories first, in walk order, awaited to completion before a
        // single file is queued. They stay out of the pool for two reasons: the
        // host refuses to invent a parent, so a file cannot be written before
        // its directory exists — and two concurrent mkdirs of one path race in
        // the script's own existence check. A mkdir is a round trip with no
        // bytes in it, so parallelising it would buy nothing anyway.
        for (const n of nodes.filter((n) => n.dir)) await makeDir(rootId, `${base}/${n.rel}`)
        for (const n of nodes.filter((n) => !n.dir && n.file)) {
          const f = await n.file!.getFile()
          queueUpload(f, `${base}/${n.rel}`, n.rel, batch)
        }
      }
    } catch (err) {
      notes.push(refusalSentence(err))
    } finally {
      setTransferNote(notes.join(' '))
      setFolderBusy(false)
    }
  }

  const fetchRows = async (picked: Row[]) => {
    if (folderBusy) return
    // A folder has to land somewhere, and a tray is not somewhere. Said plainly
    // rather than by a disabled arrow with no explanation, and rather than by
    // flattening a tree into the download folder — which is what "just download
    // the files" would silently do.
    if (picked.some((r) => r.dir) && (mode !== 'directory' || !here)) {
      setTransferNote(t('hostfiles.folderNeedsFolder'))
      return
    }
    // Nothing to write into: the browser's own download is the most any of the
    // other engines offer, and it is spaced rather than fired as a burst,
    // because a browser receiving a burst of programmatic downloads drops all
    // but the first. These never enter the queue — the setting below governs
    // uploads always and downloads only where there is a folder.
    if (mode !== 'directory' || !here) {
      saveMany(
        rootId,
        picked.filter((r) => !r.dir).map((r) => ({ path: relPath(remotePath, r.name), name: r.name })),
      )
      return
    }
    const into = here
    setFolderBusy(true)
    setTransferNote('')
    // What the mirror will not carry. Counted rather than skipped quietly: a
    // folder that arrives two files short, with nothing anywhere saying which
    // two, is the same defect as the arrow that did nothing.
    let skipped = 0
    // Every refusal this gesture produces, and not just the last one to be
    // written. One `setTransferNote` per refusal meant a folder refused whole —
    // a truncated walk — had its sentence replaced by the skipped-item count of
    // the folder after it, roughly a second later: the operator was told two
    // files had not been carried and never told a whole folder had been left.
    const notes: string[] = []
    try {
      for (const r of picked) {
        if (!r.dir) {
          queueDownload(relPath(remotePath, r.name), r.name, r.size, into)
          continue
        }
        const from = relPath(remotePath, r.name)
        const tree = await walkTree(rootId, from)
        if (tree.truncated) {
          notes.push(t('hostfiles.treeTooBig', { name: r.name }))
          continue
        }
        const base = await ensureLocalPath(into, r.name)
        for (const n of tree.entries.filter((n) => n.dir && !n.symlink)) {
          await ensureLocalPath(base, n.rel)
        }
        for (const n of tree.entries) {
          if (n.dir) continue
          // A link is reported by the walk and never followed — it may leave the
          // root, and a mirror that resolved one would write foreign bytes under
          // a name that says they came from inside. A denied name is the same
          // refusal the server would make at the download anyway.
          if (n.symlink || n.denied) {
            skipped += 1
            continue
          }
          const slash = n.rel.lastIndexOf('/')
          const target = slash < 0 ? base : await ensureLocalPath(base, n.rel.slice(0, slash))
          const leaf = slash < 0 ? n.rel : n.rel.slice(slash + 1)
          queueDownload(`${from}/${n.rel}`, leaf, n.size, target)
        }
      }
      if (skipped > 0) notes.push(t('hostfiles.someSkipped', { count: String(skipped) }))
    } catch (err) {
      notes.push(refusalSentence(err))
    } finally {
      setTransferNote(notes.join(' '))
      setFolderBusy(false)
    }
  }

  /* ---- what the remote pane can do to what it is showing (ADR-0027 §1) ---- */

  /** What the operator typed, and why the host would not take it. The refusal
   *  lives *in* the dialog: it is modal, so a sentence written to the page's
   *  notice strip behind it is one nobody can read — the strip is under the
   *  scrim and outside the accessibility tree while the dialog is open. */
  const [newFolder, setNewFolder] = useState<{ name: string; error: string } | null>(null)
  const [renaming, setRenaming] = useState<{ from: string; to: string; error: string } | null>(null)

  const pickedRemote = remoteRows.filter((r) => remotePick.has(r.name))
  /* Deletable, which is not the same as transferable. A symlink leading out of
     the root cannot be *followed*, and `removeScript` unlinks the name without
     following it — so filtering the delete on `!r.blocked` would leave a
     dangling link nobody could ever remove from this console. A
     credential-shaped name is the other case and stays refused, by the server
     as well as here. */
  const deletable = pickedRemote.filter((r) => r.blocked !== 'denied')

  const runOp = async (op: () => Promise<void>) => {
    if (folderBusy) return
    setFolderBusy(true)
    setTransferNote('')
    try {
      await op()
    } catch (err) {
      setTransferNote(refusalSentence(err))
    } finally {
      // Re-read on the way out, including after a refusal, because a refusal is
      // not the same as nothing having happened: a recursive delete that stops
      // at node 41 has already destroyed forty things, and a pane still listing
      // them is describing a host that no longer exists. Refreshing only on
      // success left exactly that screen up until somebody pressed Yenile.
      refresh()
      setFolderBusy(false)
    }
  }

  const createFolder = () =>
    void runOp(async () => {
      if (!newFolder) return
      try {
        // createDir, not makeDir: makeDir swallows `exists` because a mirror
        // asking for a directory that is already there has got what it wanted.
        // A person who typed a name has not — and through makeDir this dialog
        // closed on a taken name and reported success, handing them somebody
        // else's folder, or a *file* of that name, as though they had made it.
        await createDir(rootId, relPath(remotePath, newFolder.name.trim()))
      } catch (err) {
        // Into the dialog, the way the rename's refusal goes, and not into
        // `transferNote`: this dialog is modal, so the page's notice strip is
        // under the scrim and `aria-hidden` while it is open. A refusal written
        // there is a button that did nothing — measured, the sentence was in
        // the document with an `aria-hidden` ancestor and nothing on screen.
        // The updater form, not `{ ...newFolder }`. `newFolder` here is the
        // snapshot from the render that fired the request, and this request is
        // an SSH exec on a far host — long enough for somebody to carry on
        // typing while the button is disabled. Spreading the snapshot put the
        // name they had *left* back into the field: a later-arriving reply
        // overwriting a human's input, which is the one thing CLAUDE.md says
        // may never happen, and the same defect ADR-0027 rejects auto-suffixing
        // for. Dropping the refusal instead when the text has moved on was the
        // other option and is worse: it trades a losable message for a silent
        // button, which is what this dialog was just fixed for.
        setNewFolder((cur) => (cur === null ? cur : { ...cur, error: refusalSentence(err) }))
        return
      }
      setNewFolder(null)
    })

  const renameOne = () =>
    void runOp(async () => {
      if (!renaming) return
      try {
        await moveEntry(
          rootId,
          relPath(remotePath, renaming.from),
          relPath(remotePath, renaming.to.trim()),
        )
      } catch (err) {
        // The dialog stays open and keeps what was typed. Closing it and
        // reopening — the natural implementation — throws away the operator's
        // input, and auto-suffixing to `name-1` substitutes a name they did not
        // choose, which is the account-prefill defect in another costume.
        // Updater form, for the reason spelled out on `createFolder` above:
        // the caught closure holds the name as it was when the request left.
        setRenaming((cur) => (cur === null ? cur : { ...cur, error: refusalSentence(err) }))
        return
      }
      setRenaming(null)
      setRemotePick(new Set())
    })

  /**
   * Removing what is selected, one confined act at a time.
   *
   * The walk runs *before* the confirmation, because a confirmation that cannot
   * say what is being destroyed is a formality — and it is what refuses a
   * truncated tree, a tree above the bound, and a tree holding a name this
   * surface will not touch. Every refusal arrives before anything is gone.
   *
   * Sequential, for ADR-0026 §11's reason: every removal is an SSH exec on the
   * far host. Not in the transfer pool, because a delete is not a transfer and
   * sharing the budget would let a folder removal starve the bytes somebody is
   * waiting on.
   */
  const removeSelected = async () => {
    if (folderBusy || deletable.length === 0) return
    const plans: Array<{ name: string; order: string[]; files: number; dirs: number }> = []
    setFolderBusy(true)
    setTransferNote('')
    try {
      // Over the whole selection, not over `deletable`. `deletable` is what
      // keeps the button off when a credential-shaped name is *all* that is
      // picked; using it here as well dropped that row out of a *mixed*
      // selection instead — the confirmation counted the rest, the rest was
      // destroyed, and nothing anywhere said which name had been left behind.
      // A delete refuses the whole plan over one denied node (remove.ts says
      // why, at length); silently doing less than was asked is the version of
      // that an operator cannot act on.
      for (const r of pickedRemote) {
        const base = relPath(remotePath, r.name)
        // A symlink is one unlink whatever it points at, so only a real
        // directory is walked. Both shapes go through planRemoval so its rules
        // — the denied base above all — are applied in one place rather than
        // only on the branch that happens to have a tree. Hand-building the
        // single-node plan here instead is what made `denied` an argument whose
        // value was `false` at the only call site that passes it: a check that
        // could not fail, guarding the hole remove.ts was written about.
        const isDir = r.dir && !r.symlink
        const tree = isDir ? await walkTree(rootId, base) : { rootId, path: base, entries: [] }
        const plan = planRemoval(tree, base, { dir: isDir, denied: r.blocked === 'denied' })
        if ('refusal' in plan) {
          setTransferNote(removalRefusal(plan, r.name, t))
          return
        }
        plans.push({ name: r.name, ...plan })
      }
    } catch (err) {
      setTransferNote(refusalSentence(err))
      return
    } finally {
      setFolderBusy(false)
    }

    const files = plans.reduce((n, p) => n + p.files, 0)
    const dirs = plans.reduce((n, p) => n + p.dirs, 0)
    const ok = await confirm({
      title: t('hostfiles.removeTitle'),
      message: t('hostfiles.removeConfirm', { files: String(files), dirs: String(dirs) }),
      confirmLabel: t('hostfiles.remove'),
    })
    if (!ok) return

    await runOp(async () => {
      for (const plan of plans) {
        for (const path of plan.order) await removeEntry(rootId, path)
      }
      setRemotePick(new Set())
    })
  }

  /** The arrows act on the selection; a double-click acts on one row. Both go
   *  through the same two functions, so a folder behaves the same either way —
   *  the defect this replaces was a second path that quietly handled files
   *  only. */
  const sendSelected = () => {
    if (mode === 'tray') {
      const batch = newBatch()
      for (const f of tray.filter((f) => localPick.has(f.name))) {
        queueUpload(f, relPath(remotePath, f.name), f.name, batch)
      }
      return
    }
    void sendRows(localRows.filter((e) => localPick.has(e.name)))
  }

  const fetchSelected = () => void fetchRows(remoteRows.filter((r) => !r.blocked && remotePick.has(r.name)))

  /* What the arrows may actually do, which is not the same as what is selected.
     A control that is enabled and does nothing teaches the operator that the
     screen is broken — and it was: selecting a folder lit the arrow and clicking
     it did nothing at all, silently, because the handler filtered directories
     out. So the count here is of rows that can move, and a selection of nothing
     but refused names leaves the arrow off. */
  const movableDown = remoteRows.filter((r) => !r.blocked && remotePick.has(r.name)).length
  const movableUp =
    mode === 'tray'
      ? tray.filter((f) => localPick.has(f.name)).length
      : localRows.filter((e) => localPick.has(e.name)).length

  const active = transfers.filter(isActive)
  const failed = transfers.filter((x) => x.state === 'failed')
  /* Cancelled belongs here, and leaving it out made Durdur a control with no
     answer: the three tabs are `isActive`, `failed` and `done`, so a stopped row
     was in none of them and vanished from the interface entirely — measured,
     all three tabs read 0 and all three said "Burada bir şey yok." It also left
     `hostfiles.state.cancelled` written, translated twice and switched on in
     `stateLabel` with nothing able to render it, which is this codebase's
     recurring defect arriving through the one door a key guard cannot see.
     Not `failed`: an abort is not a failure, which is the distinction the
     upload path already refuses to blur. Finished is what it is, and the row
     says which kind — with the bytes it got to, which is what a resume wants. */
  const done = transfers.filter(
    (x) => x.state === 'done' || x.state === 'cancelled' || x.state === 'skipped',
  )
  const shown = tab === 'failed' ? failed : tab === 'done' ? done : active
  const totals = batchTotals(transfers)
  const batchRate = batchRateOf(transfers, now)
  const batchEta = batchEtaOf(transfers, now)
  /* Rows drawn, and rows counted. The cap is on the Active tab only: Failed and
     Done are lists somebody reads, and truncating those would hide the very
     rows they opened the tab for. */
  const QUEUE_ROWS = 24
  const visible = tab === 'active' ? shown.slice(0, QUEUE_ROWS) : shown
  const hidden = shown.length - visible.length

  useEffect(() => {
    if (active.length === 0) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = t('hostfiles.leaveWarning')
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [active.length, t])

  const discardPartial = async (name: string) => {
    const ok = await confirm({
      title: t('hostfiles.discard'),
      message: t('hostfiles.discardConfirm'),
      confirmLabel: t('hostfiles.discard'),
      tone: 'danger',
    })
    if (!ok) return
    await abortUpload(rootId, relPath(remotePath, name))
    refresh()
  }

  return (
    <Page
      header={
        <PageHeader
          title={t('hostfiles.title')}
          subtitle={
            root
              ? t('hostfiles.subtitle', {
                  host: root.hostName ?? '?',
                  roots: String(rootItems.length),
                })
              : t('hostfiles.noRootsShort')
          }
          actions={
            <>
              <Button size="sm" onClick={() => setManaging(true)}>
                <Icon icon={Settings2} size={14} />
                {t('hostfiles.manageRoots')}
              </Button>
              <Button size="sm" onClick={refresh} disabled={rootId === ''}>
                <Icon icon={RefreshCw} size={14} />
                {t('action.refresh')}
              </Button>
            </>
          }
        />
      }
    >
      <div
        className="k-fm"
        style={{ '--k-fm-queueh': `${Math.round(queueSplit.value)}px` } as CSSProperties}
      >
        {rootItems.length === 0 && !roots.isPending ? (
          <EmptyState
            title={t('hostfiles.noRoots')}
            hint={t('hostfiles.noRootsHint')}
            action={
              <Button variant="primary" onClick={() => setManaging(true)}>
                <Icon icon={Plus} size={14} />
                {t('hostfiles.addRoot')}
              </Button>
            }
          />
        ) : (
          <>
            <div
              className="k-fm__panes"
              /* Scaled by 100. Flex factors summing to less than 1 take only
                 that fraction of the free space and leave the rest as a dead
                 strip — 0.62fr + 0.38fr is 0.9999 and loses a sub-pixel column. */
              style={
                {
                  '--k-fm-left': `${(paneSplit.value * 100).toFixed(2)}fr`,
                  '--k-fm-right': `${((1 - paneSplit.value) * 100).toFixed(2)}fr`,
                } as CSSProperties
              }
            >
              <Pane
                title={t('hostfiles.local')}
                icon={HardDrive}
                head={
                  mode === 'directory' ? (
                    <>
                      <Button
                        size="sm"
                        onClick={async () => {
                          const dir = await pickLocalDirectory()
                          if (!dir) return
                          setLocalDir(dir)
                          setLocalTrail([])
                          setLocalPick(new Set())
                        }}
                      >
                        <Icon icon={FolderOpen} size={13} />
                        {localDir ? t('hostfiles.changeFolder') : t('hostfiles.chooseFolder')}
                      </Button>
                      {localDir && (
                        <nav className="k-fm__crumbs" aria-label={t('hostfiles.local')}>
                          <button
                            type="button"
                            className="k-fm__crumb"
                            disabled={localTrail.length === 0}
                            onClick={() => setLocalTrail((p) => p.slice(0, -1))}
                            title={t('hostfiles.up')}
                          >
                            <Icon icon={CornerLeftUp} size={13} />
                          </button>
                          <button
                            type="button"
                            className="k-fm__crumb k-mono"
                            onClick={() => setLocalTrail([])}
                          >
                            {localDir.name}
                          </button>
                          {localTrail.map((d, i) => (
                            <span key={`${d.name}-${i}`} className="k-fm__crumbwrap">
                              <Icon icon={ChevronRight} size={12} />
                              <button
                                type="button"
                                className="k-fm__crumb k-mono"
                                onClick={() => setLocalTrail((p) => p.slice(0, i + 1))}
                              >
                                {d.name}
                              </button>
                            </span>
                          ))}
                        </nav>
                      )}
                    </>
                  ) : (
                    <>
                      <label className="k-fm__pickfiles">
                        <Icon icon={Plus} size={13} />
                        {t('hostfiles.addFiles')}
                        <input
                          type="file"
                          multiple
                          className="k-visually-hidden"
                          onChange={(ev) => {
                            setTray((p) => [...p, ...Array.from(ev.target.files ?? [])])
                            ev.target.value = ''
                          }}
                        />
                      </label>
                      <Badge tone="neutral" subtle>
                        {t('hostfiles.trayMode')}
                      </Badge>
                    </>
                  )
                }
                rows={localList}
                sort={localSort}
                onSort={toggleSort(setLocalSort, localSort)}
                selected={localPick}
                onSelect={pickIn(setLocalPick)}
                onOpen={(r) => {
                  const entry = localRows.find((e) => e.name === r.name)
                  if (entry?.directory) {
                    setLocalTrail((p) => [...p, entry.directory as LocalDirHandle])
                    setLocalPick(new Set())
                  }
                }}
                onTransfer={(r) => {
                  if (!writable) return
                  if (mode === 'tray') {
                    const f = tray.find((x) => x.name === r.name)
                    if (f) queueUpload(f, relPath(remotePath, f.name), f.name, newBatch())
                    return
                  }
                  const entry = localRows.find((e) => e.name === r.name)
                  if (entry) void sendRows([entry])
                }}
                pending={localBusy}
                empty={
                  mode === 'directory' && !localDir ? t('hostfiles.chooseFolderFirst') : t('hostfiles.emptyLocal')
                }
                emptyHint={mode === 'directory' && !localDir ? t('hostfiles.chooseFolderHint') : undefined}
                onDropFiles={
                  mode === 'tray' ? (files) => setTray((p) => [...p, ...Array.from(files)]) : undefined
                }
              />

              {/* The two acts, between the two panes, pointing where the bytes
                  go. Disabled rather than hidden when nothing is selected: a
                  control that vanishes teaches nothing about why.

                  They live *inside* the separator rather than beside it: two
                  things in the middle of the screen competing for one gesture is
                  worse, and a fourth track would put the divider to one side of
                  the arrows. The hook ignores a pointerdown whose target is a
                  child, so pressing an arrow never starts a drag. */}
              <div className="k-fm__vsplit" {...(narrow ? {} : paneSplit.bind)}>
              <div
                className="k-fm__acts"
                /* The gutter's `onKeyDown` is on the separator and these
                   buttons are inside it, so every key pressed on an arrow
                   bubbles into the splitter. `useSplitter` refuses a
                   pointerdown and a double-click whose target is a child and
                   makes no such check on a key, and its Enter branch calls
                   `preventDefault()` — which is the browser's activation of a
                   focused button. Measured: tab to the arrow, press Enter, and
                   nothing transfers while the split jumps back to 0.5. Held
                   here rather than in the hook because a control hung in a
                   gutter is this screen's arrangement to answer for. */
                onKeyDown={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="k-fm__move"
                  disabled={movableUp === 0 || !writable || folderBusy}
                  onClick={sendSelected}
                  title={writable ? t('hostfiles.send') : t('hostfiles.readOnlyRoot')}
                  aria-label={t('hostfiles.send')}
                >
                  <Icon icon={ArrowRight} size={16} />
                </button>
                <button
                  type="button"
                  className="k-fm__move"
                  disabled={movableDown === 0 || folderBusy}
                  onClick={fetchSelected}
                  title={t('hostfiles.fetch')}
                  aria-label={t('hostfiles.fetch')}
                >
                  <Icon icon={ArrowLeft} size={16} />
                </button>
              </div>
              </div>

              <Pane
                title={t('hostfiles.remote')}
                icon={Server}
                head={
                  <>
                    <SelectField
                      label={t('hostfiles.root')}
                      labelHidden
                      value={rootId}
                      onChange={(v: string) => {
                        setRootId(v)
                        setRemotePath('')
                        setRemotePick(new Set())
                      }}
                      // Host and label, not host and path: the path is in the bar
                      // beside it, and printing it twice on one row spends the
                      // width the path bar actually needs.
                      options={rootItems.map((r) => ({
                        value: r.id,
                        label: `${r.hostName ?? '?'} · ${r.label || r.path}`,
                      }))}
                    />
                    {/* Paste a path and press Enter. Restored after the rewrite
                        briefly dropped it: an operator who already knows
                        /var/lib/systemd/coredump/core.foo.1234 should never have
                        to click through four directories to reach it, and no
                        shell can hand a browser that string. */}
                    <input
                      type="text"
                      className="k-fm__pathbar k-mono"
                      value={typed.value}
                      aria-label={t('hostfiles.pathBar')}
                      placeholder={t('hostfiles.pathBarHint')}
                      onChange={(ev) => {
                        typed.set(ev.target.value)
                        setPathError('')
                      }}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                          ev.preventDefault()
                          jump()
                        }
                      }}
                    />
                    <nav className="k-fm__crumbs" aria-label={t('hostfiles.remote')}>
                      <button
                        type="button"
                        className="k-fm__crumb"
                        disabled={remotePath === ''}
                        onClick={() => setRemotePath(parentOf(remotePath))}
                        title={t('hostfiles.up')}
                      >
                        <Icon icon={CornerLeftUp} size={13} />
                      </button>
                      {remotePath
                        .split('/')
                        .filter(Boolean)
                        .map((part, i, all) => (
                          <span key={`${part}-${i}`} className="k-fm__crumbwrap">
                            <Icon icon={ChevronRight} size={12} />
                            <button
                              type="button"
                              className="k-fm__crumb k-mono"
                              onClick={() => setRemotePath(all.slice(0, i + 1).join('/'))}
                            >
                              {part}
                            </button>
                          </span>
                        ))}
                    </nav>
                    {!writable && (
                      <Badge tone="neutral" subtle>
                        {t('hostfiles.readOnlyRoot')}
                      </Badge>
                    )}
                    {/* Its own strip rather than inline among the crumbs: the
                        header already carries a select, a path bar, crumbs and a
                        badge, and three more controls inline take it to three
                        wrapped lines at an ordinary window.

                        Disabled, never hidden, and disabled on what will
                        actually happen rather than on what is selected — an
                        enabled control that does nothing is the defect this
                        screen shipped once already. */}
                    <div className="k-fm__rowacts">
                      <Button
                        size="sm"
                        disabled={!writable || folderBusy}
                        onClick={() => setNewFolder({ name: '', error: '' })}
                      >
                        <Icon icon={FolderPlus} size={13} />
                        {t('hostfiles.newFolder')}
                      </Button>
                      <Button
                        size="sm"
                        disabled={!writable || folderBusy || pickedRemote.length !== 1}
                        onClick={() =>
                          setRenaming({ from: pickedRemote[0]!.name, to: pickedRemote[0]!.name, error: '' })
                        }
                      >
                        <Icon icon={PenLine} size={13} />
                        {t('hostfiles.rename')}
                      </Button>
                      <Button
                        size="sm"
                        disabled={!writable || folderBusy || deletable.length === 0}
                        onClick={() => void removeSelected()}
                      >
                        <Icon icon={Trash2} size={13} />
                        {t('hostfiles.remove')}
                      </Button>
                    </div>
                  </>
                }
                rows={remoteRows}
                sort={remoteSort}
                onSort={toggleSort(setRemoteSort, remoteSort)}
                selected={remotePick}
                onSelect={pickIn(setRemotePick)}
                onOpen={(r) => {
                  setRemotePath(relPath(remotePath, r.name))
                  setRemotePick(new Set())
                }}
                onTransfer={(r) => {
                  if (!r.blocked) void fetchRows([r])
                }}
                pending={listing.isPending && rootId !== ''}
                error={listing.error}
                onRetry={() => void listing.refetch()}
                empty={t('hostfiles.emptyRemote')}
                onDropFiles={writable ? (files) => {
                  // Its own batch: a drop is a gesture, and an "apply to the
                  // rest" answered for something else is not an answer about
                  // these files.
                  const batch = newBatch()
                  for (const f of Array.from(files)) {
                    queueUpload(f, relPath(remotePath, f.name), f.name, batch)
                  }
                } : undefined}
              />
            </div>

            {folderBusy && <Notice tone="info">{t('hostfiles.folderWorking')}</Notice>}
            {pathError && <Notice tone="warn">{pathError}</Notice>}
            {localError && <Notice tone="err">{localError}</Notice>}
            {transferNote && <Notice tone="warn">{transferNote}</Notice>}
            {listing.data?.truncated && <Notice tone="warn">{t('hostfiles.truncated')}</Notice>}

            {partials.length > 0 && (
              <ul className="k-fm__partials">
                {partials.map((p) => (
                  <li key={p.name} className="k-fm__partial">
                    <Badge tone="warn" subtle>
                      {t('hostfiles.incomplete')}
                    </Badge>
                    <span className="k-mono">{p.name}</span>
                    <span className="k-fm__sub">
                      {t('hostfiles.incompleteHint', { size: formatBytes(p.size) })}
                    </span>
                    <span className="k-fm__spacer" />
                    <Button size="sm" onClick={() => void discardPartial(p.name)}>
                      <Icon icon={Trash2} size={13} />
                      {t('hostfiles.discard')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <div className="k-fm__hsplit" {...(narrow ? {} : queueSplit.bind)} />

            <section className="k-fm__queue" aria-label={t('hostfiles.transfers')}>
              <div className="k-fm__tabs" role="tablist">
                {(['active', 'failed', 'done'] as const).map((k) => {
                  const count = k === 'failed' ? failed.length : k === 'done' ? done.length : active.length
                  return (
                    <button
                      key={k}
                      type="button"
                      role="tab"
                      aria-selected={tab === k}
                      className={clsx('k-fm__tab', tab === k && 'k-fm__tab--on')}
                      onClick={() => setTab(k)}
                    >
                      {k === 'active' && active.length > 0 && (
                        <StatusDot tone="accent" live label={t('hostfiles.tabActive')} />
                      )}
                      {t(
                        k === 'active'
                          ? 'hostfiles.tabActive'
                          : k === 'failed'
                            ? 'hostfiles.tabFailed'
                            : 'hostfiles.tabDone',
                      )}
                      <span className="k-mono"> {count}</span>
                    </button>
                  )
                })}
                <span className="k-fm__spacer" />
                {/* How many at once. Not a quota and never called one — provider
                    capacity, concurrency policy, leases and priority are four
                    different things on this control plane. The ceiling comes
                    from what a transfer costs: every one is a fresh SSH
                    handshake, and sshd starts refusing them past ten. */}
                <SelectField
                  label={t('hostfiles.parallel')}
                  labelHidden
                  value={String(limit)}
                  onChange={(v: string) => {
                    const n = Number(v)
                    setLimit(n)
                    localStorage.setItem('kimene.fm.parallel', String(n))
                  }}
                  options={PARALLEL_CHOICES.map((n) => ({
                    value: String(n),
                    label: t('hostfiles.parallelN', { n: String(n) }),
                  }))}
                />
                {active.length > 0 && (
                  <Button size="sm" onClick={cancelAll}>
                    {t('hostfiles.stopAll')}
                  </Button>
                )}
                {/* Off when there is nothing finished to clear — which on this
                    screen's first paint is always, because the queue section is
                    drawn empty rather than hidden. `cleared` filters out `done`,
                    `cancelled` and `failed`, so with none of those the press did
                    nothing at all, silently: the shape the arrow shipped once
                    and `Tümünü durdur` two lines up already avoids. Disabled
                    rather than hidden, on what the press will actually do, which
                    is the rule the pane's own act strip states. */}
                <Button
                  size="sm"
                  disabled={done.length + failed.length === 0}
                  onClick={() => dispatch({ kind: 'cleared' })}
                >
                  {t('hostfiles.clearFinished')}
                </Button>
              </div>

              {/* What the whole batch is doing. Nobody answers "how long until
                  this is over" by reading two hundred rows, and until the pool
                  there was never more than one row to read. */}
              {(totals.running > 0 || totals.queued > 0) && (
                <div className="k-fm__batch">
                  <span>
                    {t('hostfiles.batchRunning')}{' '}
                    <span className="k-fm__batchnum k-mono">{totals.running}</span>
                    {' · '}
                    {t('hostfiles.batchQueued')}{' '}
                    <span className="k-fm__batchnum k-mono">{totals.queued}</span>
                  </span>
                  <span>
                    <span className="k-fm__batchnum k-mono">
                      {formatBytes(totals.sent)} / {formatBytes(totals.bytes)}
                    </span>
                  </span>
                  {batchRate !== null && (
                    <span className="k-fm__batchnum k-mono">{formatBytes(batchRate)}/s</span>
                  )}
                  {batchEta !== null && (
                    /* Seconds, like `etaOf`, and `formatDuration` takes
                       milliseconds — so this read `90 ms` beside a row saying
                       `1dk 30sn` about the same file. The locale goes with it:
                       the parameter defaults to Turkish, which is right for the
                       source of truth and wrong for the English console. */
                    <span className="k-fm__batchnum k-mono">
                      {formatDuration(batchEta * 1000, locale)}
                    </span>
                  )}
                </div>
              )}

              {shown.length === 0 ? (
                <p className="k-fm__sub k-fm__queueempty">{t('hostfiles.queueEmpty')}</p>
              ) : (
                <ul className="k-fm__transfers">
                  {visible.map((tr) => {
                    const rate = rateOf(tr, now)
                    const eta = etaOf(tr, now)
                    const pct = tr.size > 0 ? Math.min(100, (tr.sent / tr.size) * 100) : 100
                    return (
                      <li key={tr.id} className="k-fm__transfer">
                        <Icon icon={tr.direction === 'up' ? ArrowRight : ArrowLeft} size={13} />
                        <span className="k-fm__tname k-mono">{tr.name}</span>
                        <span className="k-fm__sub">{stateLabel(tr.state, t)}</span>
                        {/* A path is a machine fact, and this row already prints
                            one in mono two cells to the left. Sans here made the
                            same kind of value read as prose. */}
                        <span className="k-fm__sub k-mono">{tr.path}</span>
                        <span className="k-fm__spacer" />
                        {tr.state === 'failed' ? (
                          <span className="k-fm__err">{tr.error}</span>
                        ) : (
                          <>
                            <span className="k-fm__bar" aria-hidden>
                              <span className="k-fm__barfill" style={{ width: `${pct}%` }} />
                            </span>
                            <span className="k-fm__num k-mono">
                              {formatBytes(tr.sent)} / {formatBytes(tr.size)}
                            </span>
                            <span className="k-fm__num k-mono">
                              {rate == null ? '—' : `${formatBytes(rate)}/s`}
                            </span>
                            <span className="k-fm__num k-mono">
                              {eta == null ? '—' : formatDuration(eta * 1000, locale)}
                            </span>
                            {/* Only on a row something can still be stopped on.
                                `cancel` dispatches `cancelled` for whatever id
                                it is handed — the pool cannot know which rows
                                are finished — so on the Bitti tab this button
                                rewrote a receipt: a transfer that had landed on
                                the host was restated as "Vazgeçildi" and left
                                every tab at once. */}
                            {isActive(tr) && (
                              <button
                                type="button"
                                className="k-fm__stop"
                                onClick={() => cancel(tr.id)}
                                title={t('hostfiles.stop')}
                                aria-label={t('hostfiles.stop')}
                              >
                                <Icon icon={X} size={13} />
                              </button>
                            )}
                          </>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
              {hidden > 0 && (
                /* The rest, as a count. Two hundred identical "Sırada" rows is a
                   wall rather than information, and re-rendering them every
                   second under the clock is sixteen hundred nodes a second
                   nobody is reading. */
                <p className="k-fm__more">{t('hostfiles.queueMore', { n: String(hidden) })}</p>
              )}
            </section>
          </>
        )}
      </div>

      {managing && <HostRootsDialog onClose={() => setManaging(false)} />}

      {newFolder !== null && (
        <Dialog
          title={t('hostfiles.newFolder')}
          onClose={() => setNewFolder(null)}
          footer={
            <Button
              variant="primary"
              /* `folderBusy` as well as the empty name: `runOp` returns without
                 a word while one is in flight, so a second press on an enabled
                 button did nothing at all — the shape this screen already
                 shipped once at the transfer arrow. */
              disabled={newFolder.name.trim() === '' || folderBusy}
              onClick={createFolder}
            >
              {t('hostfiles.newFolder')}
            </Button>
          }
        >
          <TextField
            label={t('hostfiles.newFolderName')}
            value={newFolder.name}
            onChange={(v) => setNewFolder({ ...newFolder, name: v, error: '' })}
          />
          {/* Beside the field, like the rename dialog's. A refusal put in the
              page's notice strip is behind the scrim and out of the
              accessibility tree while this is open, so the operator sees a
              button that did nothing. */}
          {newFolder.error && <Notice tone="err">{newFolder.error}</Notice>}
        </Dialog>
      )}

      {renaming && (
        <Dialog
          title={t('hostfiles.rename')}
          onClose={() => setRenaming(null)}
          footer={
            <Button
              variant="primary"
              disabled={renaming.to.trim() === '' || renaming.to === renaming.from || folderBusy}
              onClick={renameOne}
            >
              {t('hostfiles.rename')}
            </Button>
          }
        >
          <TextField
            label={t('hostfiles.renameTo')}
            value={renaming.to}
            onChange={(v) => setRenaming({ ...renaming, to: v, error: '' })}
          />
          {renaming.error && <Notice tone="err">{renaming.error}</Notice>}
        </Dialog>
      )}

      {replaceAsk && (
        /* Keyed on the question, so the "apply to the rest" tick does not carry
           over from the file before it. */
        <ReplaceDialog
          key={`${replaceAsk.batch}:${replaceAsk.name}`}
          name={replaceAsk.name}
          answer={answerReplace}
        />
      )}
    </Page>
  )
}
